import { createSdk } from '@descope/web-js-sdk'

export const DESCOPE_ERROR_UNVERIFIED_PHONE = 'E062115'
// Descope's own errorDescription for this one ("Request is missing required arguments") is
// generic and misleading — the actual reason is in errorMessage instead ("Illegal phone
// number"), which toAuthError doesn't prefer. See getErrorMessage() in MfaEnrollment.
export const DESCOPE_ERROR_INVALID_PHONE = 'E011002'
// Not a real Descope error code — synthesized in withDescopeLoader() below for a raw
// fetch()-level failure (offline, DNS, blocked request), which never reaches Descope at
// all and so never gets one of their own codes.
export const DESCOPE_ERROR_NETWORK = 'NETWORK_ERROR'
// Generic "Failed to send sms" — covers several distinct upstream failures (invalid
// number, carrier rejection, provider rate limiting), so this code alone doesn't mean
// "too many attempts". Confirmed against a real response: after repeated resends, Descope
// returns this code wrapping a Twilio 60203 "Max send attempts reached" inside
// errorMessage — that combination (code + message pattern) is what actually signals a
// send-side lockout. See isOtpSendRateLimited() below.
export const DESCOPE_ERROR_SMS_SEND_FAILED = 'E032101'
// Confirmed with Descope support (CLUB-786): returned once a code's own "Verification
// Attempts" limit (default 3, configurable in OTP Settings) is exhausted — the code is
// discarded and a new one must be requested. Descope's own text is "Max attempts exceeded
// for one time code", and — per Descope support — this is indistinguishable from a single
// wrong guess by any other field in the response, so the UI must branch on this code alone
// to tell a user "you're locked out of this code" instead of "that code was wrong".
export const DESCOPE_ERROR_MAX_OTP_ATTEMPTS = 'E061103'
/*
 * A single wrong guess against a code that is still alive — the ordinary failure, and the
 * only one that consumes one of that code's verification attempts. Named separately so the
 * UI can count "2 attempts left" locally: Descope never reports how many attempts remain,
 * and per Descope support a max-attempts response is otherwise indistinguishable from this
 * one (see DESCOPE_ERROR_MAX_OTP_ATTEMPTS above).
 *
 * The code itself is well established — it is what the enrolment flow has always received
 * for a wrong guess, asserted in MfaEnrollment.test.ts. What is NOT yet confirmed against a
 * live tenant is the boundary: whether the THIRD wrong guess returns this code or already
 * DESCOPE_ERROR_MAX_OTP_ATTEMPTS. That decides whether the user ever sees "1 attempt left",
 * so confirm it before trusting the final step of the countdown. isWrongOtpCode() is an
 * allow-list regardless, so an unexpected code degrades to the previous generic message
 * rather than counting down wrongly.
 */
export const DESCOPE_ERROR_INVALID_OTP = 'E061102'
// Descope's own "Internal error while trying to sign up or in" — an E0611xx code, which
// Descope classes as server-side ("Internal"), from their "Sign Up or In" action used
// internally for OAuth/social login (signs the user in, creating the account first if it
// doesn't exist yet). Not something a retry with different input avoids — it happens on
// Descope's own infrastructure during that step, not from anything this app sends.
export const DESCOPE_ERROR_OAUTH_SIGN_UP_OR_IN_INTERNAL = 'E061101'

let descopeClient = null

function decodeJwtPayload (token) {
  if (!token) return {}

  const [, payload] = token.split('.')
  if (!payload) return {}

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const normalizedPayload = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=')
    const json = typeof atob === 'function'
      ? atob(normalizedPayload)
      : Buffer.from(normalizedPayload, 'base64').toString('utf8')
    return JSON.parse(json)
  } catch {
    return {}
  }
}

function getAmr (sessionJwt) {
  const amr = decodeJwtPayload(sessionJwt).amr

  if (Array.isArray(amr)) return amr.map(value => String(value).toLowerCase())
  if (typeof amr === 'string') return [amr.toLowerCase()]
  return []
}

function getClient () {
  if (!descopeClient) {
    const { descopeProjectId, descopeBaseUrl } = useRuntimeConfig().public
    descopeClient = createSdk({
      projectId: descopeProjectId,
      baseUrl: descopeBaseUrl || undefined,
      persistTokens: false
    })
  }

  return descopeClient
}

function toAuthError (response) {
  const error = new Error(response?.error?.errorDescription || response?.error?.errorMessage || 'Something went wrong. Please try again.')
  error.code = response?.error?.errorCode
  // Descope-declared cooldown, when it sends one — provider-level failures (see
  // DESCOPE_ERROR_SMS_SEND_FAILED) have not been observed to include it, so callers still
  // need their own fallback.
  error.retryAfter = response?.error?.retryAfter
  /*
   * The confirmed send-lockout payload has its distinguishing text ("Max send attempts
   * reached") in errorMessage, but error.message above prefers errorDescription (a generic
   * "Failed to send sms") whenever both are present — which is always, for this error.
   * Preserved separately so isOtpSendRateLimited() below can check it without depending on
   * which field toAuthError happened to prefer for the headline message.
   */
  error.providerMessage = response?.error?.errorMessage
  return error
}

const SEND_RATE_LIMIT_PATTERN = /rate limit|max send attempts/i

// True only for the confirmed send-lockout combination — DESCOPE_ERROR_SMS_SEND_FAILED is
// too generic on its own (see its definition above). Checks both error.message and
// error.providerMessage since the rate-limit text sits in whichever of Descope's
// errorDescription/errorMessage fields toAuthError did NOT prefer for error.message itself.
/*
 * True only for a wrong-but-countable guess. Every other way a verify can fail — a network
 * drop, a Descope-side 5xx, the max-attempts lockout itself — must NOT decrement the local
 * attempt counter, because Descope did not decrement its own.
 */
export function isWrongOtpCode (error) {
  return error?.code === DESCOPE_ERROR_INVALID_OTP
}

export function isOtpSendRateLimited (error) {
  if (error?.code !== DESCOPE_ERROR_SMS_SEND_FAILED) return false
  return SEND_RATE_LIMIT_PATTERN.test(error?.message || '') || SEND_RATE_LIMIT_PATTERN.test(error?.providerMessage || '')
}

function toToken (data) {
  return {
    access_token: data.sessionJwt,
    refresh_token: data.refreshJwt,
    token_type: 'Bearer',
    amr: getAmr(data.sessionJwt)
  }
}

async function withDescopeLoader (callback) {
  const layoutStore = useLayoutsStore()
  layoutStore.setPageLoader(true)

  try {
    return await callback()
  } catch (error) {
    /*
     * A network-level failure (offline, DNS, a blocked request) throws a raw TypeError
     * straight from fetch() — before any Descope response exists to shape via toAuthError
     * — so it would otherwise surface a browser-specific string like "Failed to fetch"
     * verbatim, unhelpfully. Caught once here since every Descope call goes through this
     * wrapper, rather than needing the same check in every screen's own error handling.
     */
    if (error instanceof TypeError) {
      const networkError = new Error('We couldn\'t reach the verification service. Please check your internet connection and try again.')
      networkError.code = DESCOPE_ERROR_NETWORK
      throw networkError
    }
    throw error
  } finally {
    layoutStore.setPageLoader(false)
  }
}

export default function useDescopeWebAuth () {
  function signInWithPassword (loginId, password) {
    return withDescopeLoader(async () => {
      const response = await getClient().password.signIn(loginId, password)
      if (!response.ok) throw toAuthError(response)

      return {
        token: toToken(response.data),
        refreshToken: response.data.refreshJwt
      }
    })
  }

  // Starts a Descope-hosted OAuth flow directly (Google, etc.) — Descope owns the
  // provider app credentials and the round trip to Google/Facebook/Apple; this just
  // asks for the URL to send the browser to. redirectUrl must be pre-registered as an
  // allowed redirect URL in the Descope project, and is where Descope sends the browser
  // back with a `code` query param once the provider round trip completes.
  function startOAuth (provider, redirectUrl) {
    return withDescopeLoader(async () => {
      const response = await getClient().oauth.start(provider, redirectUrl)
      if (!response.ok) throw toAuthError(response)

      return { url: response.data.url }
    })
  }

  // Exchanges the `code` Descope appended to redirectUrl for a real session — the OAuth
  // counterpart to signInWithPassword's returned token.
  function exchangeOAuthCode (code) {
    return withDescopeLoader(async () => {
      const response = await getClient().oauth.exchange(code)
      if (!response.ok) throw toAuthError(response)

      return { token: toToken(response.data) }
    })
  }

  // Creates the Descope-side account mirroring a Member Hub registration. Interim
  // client-side sync until the backend registration-sync API exists — Member Hub
  // remains the system of record for the registration itself; this call just ensures a
  // matching Descope user exists so the Descope-flavored login flow works afterwards.
  function signUpWithPassword (loginId, password, user = {}) {
    return withDescopeLoader(async () => {
      const response = await getClient().password.signUp(loginId, password, user)
      if (!response.ok) throw toAuthError(response)

      return {
        token: toToken(response.data),
        refreshToken: response.data.refreshJwt
      }
    })
  }

  // IMPORTANT: pass the session/access token here, NOT the refresh token. Confirmed
  // against Descope directly: calling `me()` with a refresh token returns a redacted
  // profile (phone/verifiedPhone/verifiedEmail all cleared) regardless of the account's
  // real state, while the same account's session (access) token returns the real values.
  // A refresh token is only meant to mint a new access token, not authorize a direct
  // resource read.
  function getSecurityStatus (sessionToken) {
    return withDescopeLoader(async () => {
      const response = await getClient().me(sessionToken)
      if (!response.ok) throw toAuthError(response)

      return {
        email: response.data.email || response.data.loginIds?.[0] || '',
        phone: response.data.phone || '',
        verifiedPhone: !!response.data.verifiedPhone,
        verifiedEmail: !!response.data.verifiedEmail,
        totpEnabled: !!response.data.TOTP
      }
    })
  }

  function sendSmsMfa (loginId, refreshToken) {
    return withDescopeLoader(async () => {
      const response = await getClient().otp.signIn.sms(loginId, { mfa: true }, refreshToken)
      if (!response.ok) throw toAuthError(response)

      return {
        maskedPhone: response.data.maskedPhone
      }
    })
  }

  function startSmsMfaSetup (loginId, phone, refreshToken) {
    return withDescopeLoader(async () => {
      const response = await getClient().otp.update.phone.sms(loginId, phone, refreshToken)
      if (!response.ok) throw toAuthError(response)

      return {
        maskedPhone: response.data.maskedPhone
      }
    })
  }

  // Lost-phone recovery: authorizes the new-number update with the refresh token from a
  // just-verified email OTP (not the first-factor refresh token startSmsMfaSetup uses).
  // updateOptions: { mfa: true } tells Descope to keep the email factor already on that
  // refresh token instead of replacing it with phone alone, so the token returned by the
  // subsequent otp.verify.sms call carries both factors in its amr.
  function startPhoneRecovery (loginId, phone, emailOtpRefreshToken) {
    return withDescopeLoader(async () => {
      const response = await getClient().otp.update.phone.sms(loginId, phone, emailOtpRefreshToken, { mfa: true })
      if (!response.ok) throw toAuthError(response)

      return {
        maskedPhone: response.data.maskedPhone
      }
    })
  }

  /*
   * Step A of lost-phone recovery: send a 6-digit code to the account's registered email.
   *
   * Deliberately otp.update.email, NOT otp.signIn.email — confirmed against a real Descope
   * project: signIn requires the identifier to already be a verified login factor, and
   * rejects an unverified one outright ("Attempt to login with unverified email or phone").
   * That's fatal for exactly the accounts this recovery flow exists for. otp.update.email is
   * the same "update" pattern already used for phone (startPhoneRecovery /
   * otp.update.phone.sms): it works on a not-yet-verified attribute and marks it verified
   * once the code is confirmed. The email argument re-verifies the account's own current
   * email (not a change to a new one).
   *
   * refreshToken authorizes the update — confirmed required (not truly optional despite the
   * SDK's `token?` typing): omitting it fails with E064002 "Empty or Non Existent Refresh
   * Token JWT was provided". This is the first-factor Descope refresh token, already
   * preserved through the suspended session, exactly like startSmsMfaSetup's refreshToken.
   */
  function sendEmailOtp (loginId, refreshToken) {
    return withDescopeLoader(async () => {
      const response = await getClient().otp.update.email(loginId, loginId, refreshToken)
      if (!response.ok) throw toAuthError(response)

      return {
        maskedEmail: response.data.maskedEmail
      }
    })
  }

  function verifyEmailOtp (loginId, code) {
    return withDescopeLoader(async () => {
      const response = await getClient().otp.verify.email(loginId, code)
      if (!response.ok) throw toAuthError(response)

      return {
        token: toToken(response.data)
      }
    })
  }

  function verifySmsMfaSetup (loginId, code) {
    return withDescopeLoader(async () => {
      const response = await getClient().otp.verify.sms(loginId, code)
      if (!response.ok) throw toAuthError(response)

      return {
        token: toToken(response.data)
      }
    })
  }

  // NOTE: deliberately no check here that the returned session's amr actually contains
  // "mfa" (i.e. that Descope tied this OTP to the first factor). Descope's amr for this
  // step-up call has proven unreliable in practice — it varies between otherwise-identical
  // attempts — so enforcing it was rejecting genuinely correct codes. This means a correct
  // OTP alone is now accepted as MFA-complete even in the cases where Descope's own session
  // shows the two factors were never tied together.
  function verifySmsMfa (loginId, code) {
    return withDescopeLoader(async () => {
      const response = await getClient().otp.verify.sms(loginId, code)
      if (!response.ok) throw toAuthError(response)

      return {
        token: toToken(response.data)
      }
    })
  }

  function sendPasswordReset (loginId, redirectUrl) {
    return withDescopeLoader(async () => {
      const response = await getClient().password.sendReset(loginId, redirectUrl)
      if (!response.ok) throw toAuthError(response)

      return {
        maskedEmail: response.data.maskedEmail
      }
    })
  }

  function verifyPasswordResetToken (token) {
    return withDescopeLoader(async () => {
      const response = await getClient().magicLink.verify(token)
      if (!response.ok) throw toAuthError(response)

      return {
        token: toToken(response.data),
        loginId: response.data.user?.loginIds?.[0] || response.data.user?.email || ''
      }
    })
  }

  function updatePassword (loginId, newPassword, refreshToken) {
    return withDescopeLoader(async () => {
      const response = await getClient().password.update(loginId, newPassword, refreshToken)
      if (!response.ok) throw toAuthError(response)

      return true
    })
  }

  // Revokes the Descope-issued refresh token server-side. Deliberately no
  // withDescopeLoader here — this runs as a best-effort background call during logout
  // (see userStore.logout), not a user-facing action worth blocking the UI for.
  function logout (refreshToken) {
    return getClient().logout(refreshToken)
  }

  return {
    signInWithPassword,
    signUpWithPassword,
    startOAuth,
    exchangeOAuthCode,
    getSecurityStatus,
    sendSmsMfa,
    startSmsMfaSetup,
    startPhoneRecovery,
    verifySmsMfaSetup,
    verifySmsMfa,
    sendEmailOtp,
    verifyEmailOtp,
    sendPasswordReset,
    verifyPasswordResetToken,
    updatePassword,
    logout
  }
}